import React, { useEffect, useState } from 'react';
import { Card, Col, Empty, Row, Spin, Statistic, Table, Typography } from 'antd';
import api from '../../api';
import CallsDrawer from './CallsDrawer';
import WindowNote from './WindowNote';

const { Text } = Typography;

const pctCell = (v) => v == null
  ? <Text type="secondary">—</Text>
  : <span>{Number(v).toFixed(1)}%</span>;

/**
 * What the AI heard each agent do, for one campaign.
 *
 * Agent-facing measures: how often the floor pitched when it could have. The
 * business-wide readings -- why people called and how it ended -- are on the
 * Business Insights tab, because they say nothing about how an agent handled
 * a call and mixing the two invites reading one as the other.
 *
 * Unlike the Analytics page, which lays out every AI column and lets the
 * operator filter their way to a question, these are fixed: the population and
 * the measure are decided in the endpoint, so the same number means the same
 * thing every time someone opens the tab.
 */
export default function AIAgentInsightsTab({ campaignId, campaign, from, to, skills }) {
  const skill = skills?.join(',') || undefined;   // the Skill Group's skills
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill]     = useState(null);   // { title, params }

  useEffect(() => {
    if (!campaignId || !from || !to) { setData(null); return; }
    setLoading(true);
    api.get('/api/campaign-summary/upsell-insights', {
      params: { campaign_id: campaignId, start_date: from, end_date: to, skill },
    })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [campaignId, from, to, skill]);

  if (!campaignId) {
    return <Empty description="Select a campaign" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />;
  }

  // Every drill-down starts from the population the counts were built on, so
  // the drawer holds the calls behind the number that was clicked and not a
  // near miss. The narrowing values come from the same response as the counts.
  const openDrill = (title, extra) => setDrill({
    title,
    params: {
      campaign:                   campaign?.CXCampaignNo,
      start_date:                 from,
      end_date:                   to,
      skill,
      upsell_circumstance:        data?.circumstance,
      upsell_has_policy_exclude:  data?.excluded_policies,
      page_size:                  100,
      ...extra,
    },
  });

  const attemptedCols = [
    { title: '#', key: 'idx', width: 50, render: (_, __, i) => i + 1 },
    {
      title: 'Name', dataIndex: 'name', key: 'name',
      render: (v) => (
        <Typography.Link onClick={() => openDrill(v, { upselling_attempted: v })}>{v}</Typography.Link>
      ),
    },
    { title: 'Calls', dataIndex: 'calls', key: 'calls', align: 'right', width: 110,
      sorter: (a, b) => a.calls - b.calls,
      render: (v) => Number(v).toLocaleString() },
    { title: '%', dataIndex: 'pct', key: 'pct', align: 'right', width: 90, render: pctCell },
  ];

  const agentCols = [
    { title: 'Agent', dataIndex: 'agent', key: 'agent', ellipsis: true,
      sorter: (a, b) => (a.agent || '').localeCompare(b.agent || '') },
    { title: 'Calls', dataIndex: 'calls', key: 'calls', align: 'right', width: 100,
      sorter: (a, b) => a.calls - b.calls,
      render: (v) => Number(v).toLocaleString() },
    {
      title: 'Upsell Not Attempted', dataIndex: 'not_attempted', key: 'not_attempted',
      align: 'right', width: 175,
      sorter: (a, b) => a.not_attempted - b.not_attempted,
      render: (v, r) => v
        ? <Typography.Link onClick={() => openDrill(
            `${r.agent} — not attempted`,
            { agent_no: r.agent_no, upselling_attempted: data?.not_attempted })}>
            {Number(v).toLocaleString()}
          </Typography.Link>
        : <Text type="secondary">0</Text>,
    },
    {
      title: 'Upsell Attempted', dataIndex: 'attempted', key: 'attempted',
      align: 'right', width: 155,
      sorter: (a, b) => a.attempted - b.attempted,
      // Everything that is not a recorded "no attempt", which is how the
      // column is defined -- so the drill-down excludes that one value rather
      // than asking for a positive one.
      render: (v, r) => v
        ? <Typography.Link onClick={() => openDrill(
            `${r.agent} — attempted`,
            { agent_no: r.agent_no, upselling_attempted_not: data?.not_attempted })}>
            {Number(v).toLocaleString()}
          </Typography.Link>
        : <Text type="secondary">0</Text>,
    },
    { title: 'Upsell Attempt %', dataIndex: 'attempt_pct', key: 'attempt_pct',
      align: 'right', width: 150,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.attempt_pct ?? -1) - (b.attempt_pct ?? -1),
      render: pctCell },
  ];

  // No label on the total: it only fits in the '#' column, which is 50px wide
  // and broke "Grand total" across three lines. The bold figure under the
  // Calls header reads as the total without one, and the card to the left
  // states the same number in words.
  const totalRow = (cols) => (
    <Table.Summary.Row>
      {cols.map((c, i) => (
        <Table.Summary.Cell key={c.key} index={i} align={c.align}>
          {c.key === 'calls'
            ? <Text strong>{Number(data?.total || 0).toLocaleString()}</Text>
            : null}
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );

  return (
    <>
      <WindowNote from={from} to={to} />

      <Typography.Title level={5} style={{ marginTop: 8 }}>Upselling</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
        Both tables count only calls the AI marked as a valid upsell circumstance where the
        customer was not already covered for the issue, so emergencies, cancellations and
        whole-house-plan holders are out of the denominator rather than dragging the rate down.
        Upsell Attempted is those calls less the ones recorded as no attempt.
      </Typography.Paragraph>

      <Spin spinning={loading}>
        <Row gutter={16} style={{ marginBottom: 16 }} align="stretch">
          {/* The campaign's own rate, beside the breakdown it comes from. The
              denominator is named on the card: a rate over "every call" and a
              rate over "calls worth pitching on" are different numbers, and
              only the second one is a fair measure of the floor. */}
          <Col flex="230px">
            <Card size="small" style={{ height: '100%' }}>
              <Statistic
                title="Upsell Attempt %"
                value={data?.overall?.attempt_pct ?? 0}
                precision={1}
                suffix="%"
                valueStyle={{ color: '#1677ff' }}
              />
              <Statistic
                title="Upsell Attempts"
                value={data?.overall?.attempted ?? 0}
                style={{ marginTop: 12 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                of {Number(data?.overall?.calls || 0).toLocaleString()} upsellable calls
              </Text>
            </Card>
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <Card
              size="small"
              title="Upsell Attempted (for calls relevant for Upsell e.g. non emergency calls, already has whole house plan)"
              style={{ height: '100%' }}
            >
              <Table
                rowKey="name"
                size="small"
                pagination={false}
                dataSource={data?.attempted || []}
                columns={attemptedCols}
                locale={{ emptyText: 'No AI-scored calls in this window.' }}
                summary={(rows) => rows.length ? totalRow(attemptedCols) : null}
              />
            </Card>
          </Col>
        </Row>

        <Card size="small" title="Agent Upsell Attempts">
          <Table
            rowKey={(r) => r.agent_no ?? r.agent}
            size="small"
            dataSource={data?.by_agent || []}
            columns={agentCols}
            pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} agents` }}
            locale={{ emptyText: 'No AI-scored calls in this window.' }}
          />
        </Card>
      </Spin>

      <CallsDrawer drill={drill} onClose={() => setDrill(null)} />
    </>
  );
}
